import {
  createShapeId,
  toRichText,
  type TLRichText,
  type TLShapeId,
} from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { BoardDocument, Sheet } from "../shared/schema";
import { nativeDocument } from "../shared/document-session";
import { documentPosition } from "./document-placement";

function freeDocumentPosition(
  editor: Editor,
  preferred: { x: number; y: number },
  w = 560,
) {
  const occupied = editor
    .getCurrentPageShapes()
    .filter((shape) => shape.parentId === editor.getCurrentPageId())
    .flatMap((shape) => {
      const bounds = editor.getShapePageBounds(shape);
      return bounds
        ? [
            {
              x: bounds.x,
              y: bounds.y,
              w: bounds.w,
              h: nativeDocument(shape) ? Math.max(800, bounds.h) : bounds.h,
            },
          ]
        : [];
    });
  return documentPosition(preferred, { w, h: 800 }, occupied);
}

function revealDocument(editor: Editor, id: TLShapeId) {
  editor.select(id);
  const bounds = editor.getShapePageBounds(id)?.clone();
  if (bounds) bounds.h = Math.max(800, bounds.h);
  if (bounds && !editor.getViewportPageBounds().contains(bounds))
    editor.zoomToBounds(bounds, {
      inset: 80,
      targetZoom: Math.min(1, editor.getZoomLevel()),
    });
}

export function workspaceDocuments(editor: Editor) {
  return editor
    .getCurrentPageShapes()
    .filter(
      (shape): shape is BoardDocument =>
        nativeDocument(shape) && shape.parentId === editor.getCurrentPageId(),
    )
    .sort(
      (a, b) =>
        Number(a.meta.order ?? 1e9) - Number(b.meta.order ?? 1e9) ||
        a.index.localeCompare(b.index),
    );
}

export function orderedContents(editor: Editor) {
  return editor
    .getCurrentPageShapes()
    .filter(
      (shape): shape is Sheet | BoardDocument =>
        shape.parentId === editor.getCurrentPageId() &&
        (shape.type === "sheet" || nativeDocument(shape)),
    )
    .sort(
      (a, b) =>
        Number(a.meta.order ?? 1e9) - Number(b.meta.order ?? 1e9) ||
        a.index.localeCompare(b.index) ||
        a.id.localeCompare(b.id),
    );
}

export function reorderContent(
  editor: Editor,
  id: TLShapeId,
  direction: number,
) {
  const items = orderedContents(editor);
  const from = items.findIndex((shape) => shape.id === id);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= items.length) return;
  [items[from], items[to]] = [items[to], items[from]];
  editor.markHistoryStoppingPoint("Reorder contents");
  editor.updateShapes(
    items.map((shape, order) => ({
      id: shape.id,
      type: shape.type,
      meta: { ...shape.meta, order },
    })),
  );
}

function appendContentOrder(editor: Editor) {
  const items = orderedContents(editor);
  editor.updateShapes(
    items.flatMap((shape, order) =>
      shape.meta.order === order
        ? []
        : [
            {
              id: shape.id,
              type: shape.type,
              meta: { ...shape.meta, order },
            },
          ],
    ),
  );
  return items.length;
}

export function createWorkspaceDocument(
  editor: Editor,
  point?: { x: number; y: number },
  initial?: { name: string; richText: TLRichText },
) {
  const selected = editor.getOnlySelectedShape();
  const viewport = editor.getViewportPageBounds();
  const sheet = selected?.type === "sheet" ? (selected as Sheet) : null;
  const sheetBounds = sheet && editor.getShapePageBounds(sheet);
  const beside =
    sheetBounds && sheetBounds.maxX + 120 + 560 <= viewport.maxX + 120;
  const { x, y } =
    point ??
    freeDocumentPosition(editor, {
      x: beside ? sheetBounds!.maxX + 120 : viewport.center.x - 280,
      y: beside ? sheetBounds!.minY : viewport.center.y - 400,
    });
  const id = createShapeId();
  editor.markHistoryStoppingPoint("Add document");
  const order = appendContentOrder(editor);
  editor.createShape<BoardDocument>({
    id,
    type: "document",
    parentId: editor.getCurrentPageId(),
    x,
    y,
    props: {
      w: 560,
      h: 320,
      name: initial?.name.slice(0, 120) || "Untitled document",
      source: "",
      richText: initial?.richText ?? toRichText(""),
      fontSize: 18,
    },
    meta: { nativeDocument: true, documentVersion: 0, order },
  });
  revealDocument(editor, id);
  return id;
}

export function duplicateWorkspaceDocument(
  editor: Editor,
  shape: BoardDocument,
) {
  const id = createShapeId();
  const point = freeDocumentPosition(
    editor,
    { x: shape.x + shape.props.w + 64, y: shape.y },
    shape.props.w,
  );
  editor.markHistoryStoppingPoint("Duplicate document");
  const order = appendContentOrder(editor);
  editor.createShape<BoardDocument>({
    id,
    type: "document",
    parentId: editor.getCurrentPageId(),
    ...point,
    props: {
      ...shape.props,
      name: `Copy of ${shape.props.name}`.slice(0, 120),
      source: "",
    },
    meta: {
      nativeDocument: true,
      documentVersion: 0,
      order,
      documentReferences: Array.isArray(shape.meta.documentReferences)
        ? shape.meta.documentReferences
        : [],
    },
  });
  revealDocument(editor, id);
  return id;
}

type Node = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
  content?: Node[];
};
const children = (node: Node) => node.content ?? [];
function inline(node: Node): string {
  if (node.type === "hardBreak") return "  \n";
  if (node.type !== "text") return children(node).map(inline).join("");
  let value = (node.text ?? "").replace(/([\\`*_\[\]])/g, "\\$1");
  for (const mark of node.marks ?? []) {
    if (mark.type === "bold") value = `**${value}**`;
    else if (mark.type === "italic") value = `*${value}*`;
    else if (mark.type === "code") value = `\`${value}\``;
    else if (mark.type === "link" && typeof mark.attrs?.href === "string")
      value = `[${value}](${mark.attrs.href})`;
  }
  return value;
}
function blocks(nodes: Node[], prefix = ""): string {
  return nodes
    .map((node) => {
      const body = children(node);
      switch (node.type) {
        case "paragraph":
          return prefix + body.map(inline).join("");
        case "heading":
          return `${"#".repeat(Math.min(3, Math.max(1, Number(node.attrs?.level) || 2)))} ${body.map(inline).join("")}`;
        case "bulletList":
          return body.map((item) => blocks(children(item), "- ")).join("\n");
        case "orderedList":
          return body
            .map((item, i) => blocks(children(item), `${i + 1}. `))
            .join("\n");
        case "taskList":
          return body
            .map((item) =>
              blocks(children(item), `- [${item.attrs?.checked ? "x" : " "}] `),
            )
            .join("\n");
        case "blockquote":
          return blocks(body)
            .split("\n")
            .map((line) => `> ${line}`)
            .join("\n");
        case "codeBlock":
          return `\`\`\`\n${body.map((part) => part.text ?? "").join("")}\n\`\`\``;
        case "horizontalRule":
          return "---";
        default:
          return body.map(inline).join("");
      }
    })
    .filter(Boolean)
    .join(prefix ? "\n" : "\n\n");
}

export function documentMarkdown(
  document: BoardDocument,
  editor?: Editor,
  roomId?: string,
) {
  const title = document.props.name.trim() || "Untitled document";
  const content = blocks(children(document.props.richText as Node));
  const references =
    editor && roomId && Array.isArray(document.meta.documentReferences)
      ? document.meta.documentReferences
          .filter((id): id is string => typeof id === "string")
          .map((id) => {
            const sheet = editor.getShape(id as TLShapeId);
            if (sheet?.type !== "sheet") return "- Sheet unavailable";
            const url = new URL(window.location.href);
            url.search = "";
            url.searchParams.set("room", roomId);
            url.searchParams.set("sheet", id);
            url.hash = "";
            return `- [${sheet.props.name.replace(/([\\[\\]\\\\])/g, "\\$1")}](${url})`;
          })
          .join("\n")
      : "";
  return `# ${title}\n\n${content.trim()}${references ? `\n\n## Referenced sheets\n\n${references}` : ""}\n`;
}

export function downloadDocument(
  document: BoardDocument,
  editor?: Editor,
  roomId?: string,
) {
  const url = URL.createObjectURL(
    new Blob([documentMarkdown(document, editor, roomId)], {
      type: "text/markdown;charset=utf-8",
    }),
  );
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = `${document.props.name.trim().replace(/[^a-z0-9._ -]/gi, "_") || "document"}.md`;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function documentLink(roomId: string, documentId: TLShapeId) {
  const url = new URL(window.location.href);
  url.searchParams.set("room", roomId);
  url.searchParams.set("document", documentId);
  url.hash = "";
  return url.toString();
}
